import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'

// ============================================
// Enums (matching backend, serialised as strings via JsonStringEnumConverter)
// ============================================

export type DocumentInstanceStatus = 'Active' | 'Archived'
export type DocumentFieldStatus = 'Active' | 'PendingDelete' | 'Deleted'
export type DocumentFieldSourceType = 'Static' | 'TemplateVariable' | 'ContentItem'

// ============================================
// DTOs
// ============================================

export interface DocumentInstanceDto {
  id: string
  tenantId: string
  templateId: string
  templateName: string
  templateVersionNumber: number
  name: string
  status: DocumentInstanceStatus
  documentVersion: number
  lastSyncedAt?: string | null
  fieldCount: number
  pendingDeleteCount: number
  createdAt: string
  updatedAt: string
}

export interface DocumentFieldDto {
  id: string
  documentInstanceId: string
  name: string
  displayName: string
  sourceType: DocumentFieldSourceType
  sourceRef?: string | null
  value?: string | null
  contentHash: string
  version: number
  status: DocumentFieldStatus
  updatedAt: string
  updatedByUserId?: string | null
}

export interface DocumentInstanceDetailDto extends DocumentInstanceDto {
  fields: DocumentFieldDto[]
}

export interface CreateDocumentRequest {
  templateId: string
  name: string
}

export interface UpdateDocumentFieldRequest {
  value: string
  expectedVersion: number
}

export interface DocumentsODataResponse {
  value: DocumentInstanceDto[]
  '@odata.count'?: number
}

/**
 * Thrown when a field update is rejected because the server-side version
 * no longer matches `expectedVersion` (HTTP 409). Carries the current
 * server-side field so callers can show the latest value.
 */
export class DocumentFieldConflictError extends Error {
  currentField?: DocumentFieldDto

  constructor(currentField?: DocumentFieldDto) {
    super('The field was changed by someone else. Please review and try again.')
    this.name = 'DocumentFieldConflictError'
    this.currentField = currentField
  }
}

function isConflictResponse(error: unknown): error is { response: { status: number; data?: DocumentFieldDto } } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === 409
  )
}

export const documentService = {
  /**
   * Gets document instances with OData query support.
   */
  async getDocuments(params?: ODataQueryParams): Promise<DocumentsODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<DocumentsODataResponse | DocumentInstanceDto[]>(
        `/v1/documents${queryString}`
      )

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data,
          '@odata.count': response.data.length,
        }
      }
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getDocument(id: string): Promise<DocumentInstanceDetailDto> {
    try {
      const response = await api.get<DocumentInstanceDetailDto>(`/v1/documents/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async createDocument(data: CreateDocumentRequest): Promise<DocumentInstanceDetailDto> {
    try {
      const response = await api.post<DocumentInstanceDetailDto>('/v1/documents', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Archives a document instance (DELETE /v1/documents/{id}).
   */
  async archiveDocument(id: string): Promise<void> {
    try {
      await api.delete(`/v1/documents/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getFields(id: string): Promise<DocumentFieldDto[]> {
    try {
      const response = await api.get<DocumentFieldDto[]>(`/v1/documents/${id}/fields`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates a field's value. Throws DocumentFieldConflictError (with the
   * server's current field, when provided) on an HTTP 409 version mismatch.
   */
  async updateField(
    documentId: string,
    fieldId: string,
    data: UpdateDocumentFieldRequest
  ): Promise<DocumentFieldDto> {
    try {
      const response = await api.put<DocumentFieldDto>(
        `/v1/documents/${documentId}/fields/${fieldId}`,
        data
      )
      return response.data
    } catch (error) {
      if (isConflictResponse(error)) {
        throw new DocumentFieldConflictError(error.response.data)
      }
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Confirms deletion of a PendingDelete field (Status -> Deleted).
   */
  async confirmDeleteField(documentId: string, fieldId: string): Promise<void> {
    try {
      await api.delete(`/v1/documents/${documentId}/fields/${fieldId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Restores a PendingDelete field back to Active.
   */
  async restoreField(documentId: string, fieldId: string): Promise<DocumentFieldDto> {
    try {
      const response = await api.post<DocumentFieldDto>(
        `/v1/documents/${documentId}/fields/${fieldId}/restore`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
